import { DateTime } from 'luxon'

import type { ActivationGroup } from '@/features/activationGroup/types'
import type { FormattedRow } from '@/features/bessDashboard/types'
import { BidType, MARKET_TIMEZONE, Status } from '@/features/bidding/constants'
import type { ExtendedBid, ExtendedBidPtu, PtuChunk } from '@/features/bidding/types/bid'
import type { BidLevelActivationGroup, BidLevelInput } from '@/features/bidding/types/bidLadderInput'
import type { PricingStrategyConfig } from '@/features/bidding/types/forecastPricing'
import type { HourlyPrices } from '@/features/bidding/utils/cascading/bidLadder/deriveForecastPrices'
import {
  deriveForecastPricesForDirection,
  getFallbackDailyPrice,
  getPriceForHour,
} from '@/features/bidding/utils/cascading/bidLadder/deriveForecastPrices'
import type { MarketDate } from '@/features/bidding/utils/date/marketDate'
import { megawattHoursToWattHours } from '@/utils/powerEnergyTransformations'

const HOURS_PER_DAY = 24
const VOLUME_UNIT = 'WATTS'

export type ValidationErrorType = 'MISSING_PRICE' | 'INVALID_CAPACITY' | 'MISSING_ACTIVATION_GROUP'

export type ValidationError = {
  type: ValidationErrorType
  levelId: string
  groupUuid: string
  groupCode: string
  message: string
}

export type TransformBidLevelsResult = {
  bids: ExtendedBid[]
  errors: ValidationError[]
}

type TransformBidLevelsParams = {
  levels: BidLevelInput[]
  deliveryDay: MarketDate
  activationGroupsMap: Map<string, ActivationGroup>
  /** Optional pricing strategy config for forecast-based pricing */
  pricingConfig?: PricingStrategyConfig
  /** Optional forecast data required when using forecast pricing mode */
  forecastsData?: FormattedRow[]
  /** Selected bid date for forecast price derivation */
  selectedDeliveryDay?: DateTime
  /** Optional set of excluded PTU groups (key format: "${groupUuid}:${hour}") */
  excludedPtuGroups?: Set<string>
}

/**
 * Transforms bid level state into ExtendedBid objects ready for API submission.
 *
 * For each assigned activation group across all levels:
 * - Creates one ExtendedBid with 24 hourly PTUs
 * - Uses the level's price based on group direction (upPrice for 'up', downPrice for 'down')
 *   OR derives hourly prices from forecast data when in forecast mode
 * - Converts capacity from MW to WATTS
 * - Each PTU includes ptuChunks with the offered price
 *
 * @returns Object containing valid bids and any validation errors
 */
export function transformBidLevelsToExtendedBids({
  levels,
  deliveryDay,
  activationGroupsMap,
  pricingConfig,
  forecastsData,
  selectedDeliveryDay,
  excludedPtuGroups,
}: TransformBidLevelsParams): TransformBidLevelsResult {
  const bids: ExtendedBid[] = []
  const errors: ValidationError[] = []

  // Track which groups have been processed (assigned to levels)
  const processedGroupUuids = new Set<string>()

  for (let levelIndex = 0; levelIndex < levels.length; levelIndex++) {
    const level = levels[levelIndex]
    const levelPricingConfig = pricingConfig?.levels[levelIndex]
    const isForecastMode = levelPricingConfig?.mode === 'forecast'

    // Derive hourly prices for both UP and DOWN directions if in forecast mode
    // Each direction uses the appropriate market forecast (UP forecast for UP bids, DOWN for DOWN)
    let upHourlyPrices: HourlyPrices | undefined
    let downHourlyPrices: HourlyPrices | undefined
    if (isForecastMode && levelPricingConfig && forecastsData && selectedDeliveryDay) {
      upHourlyPrices = deriveForecastPricesForDirection(
        forecastsData,
        levelPricingConfig.market,
        'up',
        levelPricingConfig.quantile,
        selectedDeliveryDay,
      )
      downHourlyPrices = deriveForecastPricesForDirection(
        forecastsData,
        levelPricingConfig.market,
        'down',
        levelPricingConfig.quantile,
        selectedDeliveryDay,
      )
    }

    for (const assignedGroup of level.assignedGroups) {
      processedGroupUuids.add(assignedGroup.uuid)

      // Select the appropriate hourly prices based on the group's direction
      const directionHourlyPrices = assignedGroup.direction === 'up' ? upHourlyPrices : downHourlyPrices

      const validationResult = validateGroup(
        level,
        assignedGroup,
        activationGroupsMap,
        isForecastMode,
        directionHourlyPrices,
      )

      if (!validationResult.isValid) {
        errors.push(validationResult.error)
        continue
      }

      const { activationGroup } = validationResult

      const bid = createBidForGroup({
        assignedGroup,
        activationGroup,
        deliveryDay,
        fixedPrice: validationResult.price,
        hourlyPrices: isForecastMode ? directionHourlyPrices : undefined,
        excludedPtuGroups,
      })

      bids.push(bid)
    }
  }

  // Create zero bids for unassigned groups
  for (const [uuid, activationGroup] of activationGroupsMap) {
    if (!processedGroupUuids.has(uuid)) {
      const zeroBid = createZeroBidForGroup({
        activationGroup,
        deliveryDay,
      })
      bids.push(zeroBid)
    }
  }

  return { bids, errors }
}

type ValidationSuccess = {
  isValid: true
  activationGroup: ActivationGroup
  price: number
}

type ValidationFailure = {
  isValid: false
  error: ValidationError
}

function validateGroup(
  level: BidLevelInput,
  assignedGroup: BidLevelActivationGroup,
  activationGroupsMap: Map<string, ActivationGroup>,
  isForecastMode: boolean = false,
  hourlyPrices?: HourlyPrices,
): ValidationSuccess | ValidationFailure {
  // Check if activation group exists in the map
  const activationGroup = activationGroupsMap.get(assignedGroup.uuid)
  if (!activationGroup) {
    return {
      isValid: false,
      error: {
        type: 'MISSING_ACTIVATION_GROUP',
        levelId: level.id,
        groupUuid: assignedGroup.uuid,
        groupCode: assignedGroup.code,
        message: `Activation group "${assignedGroup.code}" not found`,
      },
    }
  }

  // Check if capacity is valid
  if (assignedGroup.capacityMW <= 0) {
    return {
      isValid: false,
      error: {
        type: 'INVALID_CAPACITY',
        levelId: level.id,
        groupUuid: assignedGroup.uuid,
        groupCode: assignedGroup.code,
        message: `Group "${assignedGroup.code}" has invalid capacity: ${assignedGroup.capacityMW} MW`,
      },
    }
  }

  // In forecast mode, check if we have hourly prices
  if (isForecastMode) {
    if (!hourlyPrices || hourlyPrices.prices.size === 0) {
      return {
        isValid: false,
        error: {
          type: 'MISSING_PRICE',
          levelId: level.id,
          groupUuid: assignedGroup.uuid,
          groupCode: assignedGroup.code,
          message: `No forecast prices available for group "${assignedGroup.code}"`,
        },
      }
    }

    // Check for complete hourly data
    if (!hourlyPrices.isComplete) {
      return {
        isValid: false,
        error: {
          type: 'MISSING_PRICE',
          levelId: level.id,
          groupUuid: assignedGroup.uuid,
          groupCode: assignedGroup.code,
          message: `Incomplete forecast data for group "${assignedGroup.code}" (${hourlyPrices.prices.size}/${hourlyPrices.dayHours} hours available)`,
        },
      }
    }

    // Only reach here if all hours are available (isComplete check passed above)
    const fallbackPrice = getFallbackDailyPrice(hourlyPrices)
    if (fallbackPrice === null) {
      // This should never happen since isComplete guarantees prices exist
      return {
        isValid: false,
        error: {
          type: 'MISSING_PRICE',
          levelId: level.id,
          groupUuid: assignedGroup.uuid,
          groupCode: assignedGroup.code,
          message: `Unexpected error: could not calculate fallback price for group "${assignedGroup.code}"`,
        },
      }
    }
    return {
      isValid: true,
      activationGroup,
      price: fallbackPrice,
    }
  }

  // Check if price is set for the group's direction (fixed mode)
  const price = assignedGroup.direction === 'up' ? level.upPrice : level.downPrice
  if (price === null) {
    return {
      isValid: false,
      error: {
        type: 'MISSING_PRICE',
        levelId: level.id,
        groupUuid: assignedGroup.uuid,
        groupCode: assignedGroup.code,
        message: `Level is missing ${assignedGroup.direction.toUpperCase()} price for group "${assignedGroup.code}"`,
      },
    }
  }

  return {
    isValid: true,
    activationGroup,
    price,
  }
}

type CreateBidParams = {
  assignedGroup: BidLevelActivationGroup
  activationGroup: ActivationGroup
  deliveryDay: MarketDate
  /** Fixed price for all hours (used in fixed mode) */
  fixedPrice: number
  /** Hourly prices (used in forecast mode, overrides fixedPrice when available) */
  hourlyPrices?: HourlyPrices
  /** Optional excluded PTU groups for per-hour volume zeroing */
  excludedPtuGroups?: Set<string>
}

function createBidForGroup({
  assignedGroup,
  activationGroup,
  deliveryDay,
  fixedPrice,
  hourlyPrices,
  excludedPtuGroups,
}: CreateBidParams): ExtendedBid {
  const volumeInWatts = megawattHoursToWattHours(assignedGroup.capacityMW)

  const ptus = generate24HourPtus(
    deliveryDay,
    volumeInWatts,
    fixedPrice,
    hourlyPrices,
    excludedPtuGroups ? { groupUuid: assignedGroup.uuid, excludedPtuGroups } : undefined,
  )

  return {
    portfolio: activationGroup.portfolio,
    deliveryDay,
    createdAt: DateTime.now().setZone(MARKET_TIMEZONE),
    // Use the activation group's market program instead of hardcoding
    marketProgram: activationGroup.marketProgram,
    status: Status.DRAFT,
    activationGroupUuid: assignedGroup.uuid,
    bidType: BidType.CAPACITY,
    ptus,
  }
}

type CreateZeroBidParams = {
  activationGroup: ActivationGroup
  deliveryDay: MarketDate
}

function createZeroBidForGroup({ activationGroup, deliveryDay }: CreateZeroBidParams): ExtendedBid {
  const volumeInWatts = 0
  const price = 0

  const ptus = generate24HourPtus(deliveryDay, volumeInWatts, price)

  return {
    portfolio: activationGroup.portfolio,
    deliveryDay,
    createdAt: DateTime.now().setZone(MARKET_TIMEZONE),
    marketProgram: activationGroup.marketProgram,
    status: Status.DRAFT,
    activationGroupUuid: activationGroup.uuid,
    bidType: BidType.CAPACITY,
    ptus,
  }
}

function generate24HourPtus(
  deliveryDay: MarketDate,
  volumeInWatts: number,
  fixedPrice: number,
  hourlyPrices?: HourlyPrices,
  exclusion?: { groupUuid: string; excludedPtuGroups: Set<string> },
): ExtendedBidPtu[] {
  const startOfDay = deliveryDay.getStartOfDay()
  const ptus: ExtendedBidPtu[] = []

  for (let hour = 0; hour < HOURS_PER_DAY; hour++) {
    const ptuStart = startOfDay.plus({ hours: hour })
    const ptuEnd = startOfDay.plus({ hours: hour + 1 })

    const isExcluded = exclusion?.excludedPtuGroups.has(`${exclusion.groupUuid}:${hour}`) ?? false
    const hourVolume = isExcluded ? 0 : volumeInWatts

    // Use hourly price if available, otherwise fall back to fixed price
    const hourPrice = hourlyPrices ? (getPriceForHour(hourlyPrices, hour) ?? fixedPrice) : fixedPrice

    const ptuChunk: PtuChunk = {
      offeredVolume: { quantity: hourVolume, unit: VOLUME_UNIT },
      offeredPrice: hourPrice,
    }

    ptus.push({
      ptu: {
        start: ptuStart,
        end: ptuEnd,
      },
      offeredVolume: { quantity: hourVolume, unit: VOLUME_UNIT },
      ptuChunks: [ptuChunk],
    })
  }

  return ptus
}
